import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import pickBy from 'lodash/pickBy';
import {Workday, WorkspaceReservation} from '../entities';
import {workdaysAdapter} from '../workday/adapters';
import {
  acceptConnection,
  deleteConnection,
  ignoreConnection,
  loadConnections,
  loadConnectionSuggestions,
  loadWorkspaceReservationsForConnections,
  searchConnections,
  sendConnection,
  sendConnectionSuggestionFeedback,
} from './actions';
import {
  connectedUsersAdapter,
  connectionsAdapter,
  connectionSearchResultsAdapter,
  connectionsWorkdaysAdapter,
  connectionsWorkspaceReservationsAdapter,
} from './adapters';
import {initialState} from './initialState';

const slice = createSlice({
  name: 'connections',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadWorkspaceReservationsForConnections.pending, (state) => {
        state.isLoadingWorkspaceReservationsForConnections = true;
      })
      .addCase(loadWorkspaceReservationsForConnections.fulfilled, (state, action) => {
        const {workspaceReservations, workdays} = action.payload;
        // Note: this is a duplication of data in the store, which does not follow best practices.
        // Reason: the "normalizing" of the data before putting it in the store is super
        //         annoying to work with; most complex selectors that we have are basically returning
        //         the data very close to its original state
        state.connectionsWithReservations = action.payload.originalData;
        connectionsWorkspaceReservationsAdapter.setAll(state.workspaceReservations, workspaceReservations ?? []);
        connectionsWorkdaysAdapter.setAll(state.workdays, workdays ?? []);
        state.isLoadingWorkspaceReservationsForConnections = false;
      })
      .addCase(loadWorkspaceReservationsForConnections.rejected, (state) => {
        state.isLoadingWorkspaceReservationsForConnections = false;
      })
      .addCase(loadConnections.pending, (state) => {
        state.isLoadingConnections = true;
      })
      .addCase(loadConnections.fulfilled, (state, action) => {
        const {connections, connectionUsers} = action.payload;
        connectionsAdapter.setAll(state.connections, connections ?? {});
        connectedUsersAdapter.setAll(state.connectedUsers, connectionUsers ?? {});
        state.isLoadingConnections = false;
      })
      .addCase(loadConnections.rejected, (state) => {
        state.isLoadingConnections = false;
      })
      .addCase(sendConnection.pending, (state, action) => {
        const {id: userId} = action.meta.arg;
        connectionSearchResultsAdapter.updateOne(state.searchResults, {id: userId, changes: {isConnecting: true}});
        state.isConnecting = true;
      })
      .addCase(sendConnection.fulfilled, (state, action) => {
        const {id: userId} = action.meta.arg;
        const {connections, connectionUsers} = action.payload;
        const sentConnectionId = Object.keys(connections)[0];

        connectionsAdapter.addMany(state.connections, connections ?? {});
        connectedUsersAdapter.addMany(state.connectedUsers, connectionUsers ?? {});
        connectionSearchResultsAdapter.updateOne(state.searchResults, {
          id: userId,
          changes: {
            isConnecting: false,
            connectionId: sentConnectionId,
          },
        });
        state.isConnecting = false;
      })
      .addCase(sendConnection.rejected, (state, action) => {
        const {id: userId} = action.meta.arg;
        connectionSearchResultsAdapter.updateOne(state.searchResults, {id: userId, changes: {isConnecting: false}});
        state.isConnecting = false;
      })
      .addCase(deleteConnection.pending, (state, action) => {
        const {connectionId} = action.meta.arg;
        connectionsAdapter.updateOne(state.connections, {id: connectionId as string, changes: {isDeleting: true}});
      })
      .addCase(deleteConnection.fulfilled, (state, action) => {
        const {id: userId, connectionId} = action.meta.arg;
        const workspaceReservations = pickBy<WorkspaceReservation>(
          state.workspaceReservations.entities,
          (workspaceReservation) => workspaceReservation.userId === userId,
        );
        connectionsWorkspaceReservationsAdapter.removeMany(
          state.workspaceReservations,
          Object.keys(workspaceReservations),
        );

        const workdays = pickBy<Workday>(state.workdays.entities, (workday) => workday.userId === userId);
        workdaysAdapter.removeMany(state.workdays, Object.keys(workdays));

        connectedUsersAdapter.removeOne(state.connectedUsers, userId);
        connectionsAdapter.removeOne(state.connections, connectionId as string);
      })
      .addCase(deleteConnection.rejected, (state, action) => {
        const {connectionId} = action.meta.arg;
        connectionsAdapter.updateOne(state.connections, {id: connectionId as string, changes: {isDeleting: false}});
      })
      .addCase(acceptConnection.pending, (state, action) => {
        const {connectionId} = action.meta.arg;
        connectionsAdapter.updateOne(state.connections, {id: connectionId as string, changes: {isAccepting: true}});
      })
      .addCase(acceptConnection.fulfilled, (state, action) => {
        const {connectionId} = action.meta.arg;

        connectionsAdapter.updateOne(state.connections, {
          id: connectionId as string,
          changes: {
            isAccepting: false,
            status: 'Accepted',
          },
        });
      })
      .addCase(acceptConnection.rejected, (state, action) => {
        const {connectionId} = action.meta.arg;
        connectionsAdapter.updateOne(state.connections, {id: connectionId as string, changes: {isAccepting: false}});
      })
      .addCase(ignoreConnection.pending, (state, action) => {
        const {connectionId} = action.meta.arg;
        connectionsAdapter.updateOne(state.connections, {id: connectionId as string, changes: {isIgnoring: true}});
      })
      .addCase(ignoreConnection.fulfilled, (state, action) => {
        const {id: userId, connectionId} = action.meta.arg;
        const workspaceReservations = pickBy<WorkspaceReservation>(
          state.workspaceReservations.entities,
          (workspaceReservation) => workspaceReservation.userId === userId,
        );

        connectionsWorkspaceReservationsAdapter.removeMany(
          state.workspaceReservations,
          Object.keys(workspaceReservations),
        );
        connectedUsersAdapter.removeOne(state.connectedUsers, userId);
        connectionsAdapter.removeOne(state.connections, connectionId as string);
      })
      .addCase(ignoreConnection.rejected, (state, action) => {
        const {connectionId} = action.meta.arg;
        connectionsAdapter.updateOne(state.connections, {id: connectionId as string, changes: {isIgnoring: false}});
      })
      .addCase(searchConnections.pending, (state) => {
        state.isSearching = true;
      })
      .addCase(searchConnections.fulfilled, (state, action) => {
        state.isSearching = false;
        connectionSearchResultsAdapter.setAll(state.searchResults, action.payload ?? []);
      })
      .addCase(searchConnections.rejected, (state) => {
        state.isSearching = false;
      })
      .addCase(loadConnectionSuggestions.pending, (state) => {
        state.isLoadingConnectionSuggestions = true;
      })
      .addCase(loadConnectionSuggestions.fulfilled, (state, action) => {
        state.isLoadingConnectionSuggestions = false;
        state.connectionSuggestion = action.payload;
      })
      .addCase(loadConnectionSuggestions.rejected, (state) => {
        state.isLoadingConnectionSuggestions = false;
      })
      .addCase(sendConnectionSuggestionFeedback.pending, (state) => {
        state.isSendingConnectionSuggestionsFeedback = true;
      })
      .addMatcher(
        isAnyOf(sendConnectionSuggestionFeedback.fulfilled, sendConnectionSuggestionFeedback.rejected),
        (state) => {
          state.isSendingConnectionSuggestionsFeedback = false;
        },
      );
  },
});

export const {reducer: connectionsReducer} = slice;
